import { useCallback, type RefObject } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useGetGlobalSearchResultsPresenter } from './useGetGlobalSearchResultsPresenter'
import { GuidedIntakePromoBanner } from '../../../../components/GuidedIntakePromoBanner'
import { LlmConversationConfirmation } from '../../../../components/LlmConversationConfirmation'
import { useGuidedIntakePromoDismissal } from '../../../../hooks/useGuidedIntakePromoDismissal'
import { type RecentConversation } from '../../../../value-objects/RecentConversation'
import { GlobalSearchView } from '../../views/GlobalSearchView/GlobalSearchView'

interface IGetGlobalSearchResultsPresenterProps {
  onClose: () => void
  inputRef?: RefObject<HTMLInputElement>
  onHandoff?: (query: string) => void
  showGuidedIntakePromo?: boolean
  onGuidedIntakePromoClick: () => void
  onResumeConversation?: (conversationId: string) => void
}

export const GetGlobalSearchResultsPresenter = ({
  onClose,
  inputRef: externalInputRef,
  onHandoff,
  showGuidedIntakePromo = false,
  onGuidedIntakePromoClick,
  onResumeConversation,
}: IGetGlobalSearchResultsPresenterProps) => {
  const intl = useProcurifyIntl()
  const {
    commands: {
      changeQuery,
      openHighlightedOrSubmit,
      openRecentItem,
      openResultItem,
      refreshRecentConversation,
      selectTab,
      navigateDown,
      navigateUp,
    },
    data: {
      query,
      submittedQuery,
      recentItems,
      recentConversations,
      resultItems,
      tabs,
      inputRef,
      highlightedIndex,
      activeDescendantId,
      isExpanded,
      originalQuery,
    },
    states: {
      showEnterHint,
      contentState,
      selectedTab,
      showTruncatedNotice,
      noResultsMessage,
      unsupportedQueryMessage,
      errorMessage,
      showLlmConfirmation,
    },
  } = useGetGlobalSearchResultsPresenter(onClose, externalInputRef)

  const { isDismissed: isPromoDismissed } = useGuidedIntakePromoDismissal()

  const handleConfirmationAccept = useCallback(() => {
    onHandoff?.(originalQuery ?? submittedQuery)
  }, [onHandoff, originalQuery, submittedQuery])

  // The view swaps the whole content area for the paywall banner, so a
  // dismissed promo has to read as absent here rather than render nothing.
  const showPromo = showGuidedIntakePromo && !isPromoDismissed

  const paywallBanner =
    showPromo && showLlmConfirmation ? (
      <GuidedIntakePromoBanner
        analyticsId='global-command-gi-promo-banner-paywall'
        onLearnMoreClick={onGuidedIntakePromoClick}
      />
    ) : undefined

  const promoBanner = showPromo ? (
    <GuidedIntakePromoBanner
      analyticsId='global-command-gi-promo-banner-default'
      onLearnMoreClick={onGuidedIntakePromoClick}
    />
  ) : undefined

  const handleRecentConversationClick = useCallback(
    (conversation: RecentConversation) => {
      refreshRecentConversation(conversation)
      onResumeConversation?.(conversation.conversationId)
    },
    [refreshRecentConversation, onResumeConversation]
  )

  if (showLlmConfirmation && onHandoff) {
    return (
      <LlmConversationConfirmation
        query={query}
        inputRef={inputRef}
        title={intl.formatMessage(messages.confirmationTitle)}
        actionContent={intl.formatMessage(messages.confirmationAction)}
        actionAriaLabel={intl.formatMessage(messages.confirmationAction)}
        actionAnalyticsId='global-command-llm-confirmation-action'
        onChange={changeQuery}
        onEnter={openHighlightedOrSubmit}
        onActionClick={handleConfirmationAccept}
      />
    )
  }

  return (
    <GlobalSearchView
      query={query}
      showEnterHint={showEnterHint}
      contentState={contentState}
      recentItems={recentItems}
      recentConversations={recentConversations}
      resultItems={resultItems}
      tabs={tabs}
      selectedTab={selectedTab}
      showTruncatedNotice={showTruncatedNotice}
      noResultsMessage={noResultsMessage}
      unsupportedQueryMessage={unsupportedQueryMessage}
      errorMessage={errorMessage}
      inputRef={inputRef}
      highlightedIndex={highlightedIndex}
      activeDescendantId={activeDescendantId}
      isExpanded={isExpanded}
      promoBanner={promoBanner}
      paywallBanner={paywallBanner}
      onQueryChange={changeQuery}
      onEnter={openHighlightedOrSubmit}
      onNavigateDown={navigateDown}
      onNavigateUp={navigateUp}
      onRecentItemClick={openRecentItem}
      onResultItemClick={openResultItem}
      onTabChange={selectTab}
      onSuggestedPromptClick={onHandoff}
      onRecentConversationClick={
        onResumeConversation ? handleRecentConversationClick : undefined
      }
    />
  )
}
